import { useState } from "react";
import { Mail, MapPin, Clock, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const { t } = useTranslation();

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || t("contact.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="contact-page">
      <div className="border-b border-white/10 pb-8 mb-12">
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
          {t("contact.eyebrow")}
        </div>
        <h1 className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tighter text-white">
          {t("contact.title")}
        </h1>
        <p className="mt-4 text-base text-white/60 max-w-xl">
          {t("contact.intro")}
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-12">
        <aside className="lg:col-span-1 space-y-8">
          <div className="border border-white/15 bg-[#0A2A4F] p-6">
            <Mail size={22} strokeWidth={1.25} className="text-[#C5CDD8]" />
            <div className="mt-3 text-[10px] font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
              {t("contact.official_email")}
            </div>
            <a
              href="mailto:nordstellatech@gmail.com"
              className="mt-2 block font-display text-xl font-bold tracking-tight text-white hover:text-[#C5CDD8] transition break-all"
              data-testid="contact-official-email"
            >
              nordstellatech@gmail.com
            </a>
            <p className="mt-3 text-xs text-white/60 leading-relaxed">
              {t("contact.reply_time")}
            </p>
          </div>

          {[
            { i: MapPin, t: t("contact.address"), v: t("contact.address_value") },
            { i: Clock, t: t("contact.hours"), v: t("contact.hours_value") },
          ].map((item, i) => (
            <div key={i} className="border-t border-white/10 pt-6">
              <item.i size={20} strokeWidth={1.25} className="text-[#C5CDD8]" />
              <div className="mt-3 text-xs font-bold uppercase tracking-[0.25em] text-white">{item.t}</div>
              <div className="mt-1 text-sm text-white/60 whitespace-pre-line">{item.v}</div>
            </div>
          ))}
        </aside>

        <form onSubmit={submit} className="lg:col-span-2 space-y-6" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label={t("contact.name")} value={form.name} onChange={update("name")} testId="contact-name" required />
            <Field label={t("contact.email")} type="email" value={form.email} onChange={update("email")} testId="contact-email" required />
          </div>
          <Field label={t("contact.subject")} value={form.subject} onChange={update("subject")} testId="contact-subject" required />
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C5CDD8]">{t("contact.message")}</span>
            <textarea
              required
              rows={8}
              value={form.message}
              onChange={update("message")}
              data-testid="contact-message"
              className="mt-2 w-full border border-white/20 bg-[#0A2A4F] text-white px-4 py-3 text-sm focus:outline-none focus:border-white"
            />
          </label>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 border border-white bg-white text-[#001F3F] hover:bg-transparent hover:text-white transition-all uppercase tracking-[0.25em] text-xs font-bold px-8 py-4 disabled:opacity-60"
            data-testid="contact-submit"
          >
            {submitting ? t("contact.sending") : t("contact.send")}
            {!submitting && <Send size={14} />}
          </button>
          <p className="text-xs text-white/60">
            {t("contact.consent_pre")}{" "}
            <a href="/legal/privacy" className="underline text-white">{t("footer.privacy")}</a>.
          </p>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId, required }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C5CDD8]">{label}</span>
      <input
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="mt-2 w-full border-b border-white/40 bg-transparent text-white px-0 py-2 text-sm focus:outline-none focus:border-white"
      />
    </label>
  );
}
